/**
 * `encodePathName` — encode a user-supplied name for use as a URL PATH
 * SEGMENT on endpoints that identify a resource by its name.
 *
 * It percent-encodes TWICE, on purpose.
 *
 * Uvicorn percent-decodes the request path ONCE before Starlette routes
 * on it. So a single-encoded `/` inside a name (`%2F`) is decoded back
 * into a real path separator, which splits the URL into an extra
 * segment and matches no route at all — FastAPI's `{name}` path param
 * is `[^/]+`. The caller gets a bare `404 {"detail":"Not Found"}` that
 * looks like "the station doesn't exist" but is really "the route
 * doesn't exist" (#1521).
 *
 * That is not hypothetical: the SGH pilot has stations named
 * `Ward / EOT (admin)` and `KKH – OT/Clinic`, and every attempt to edit
 * them 404'd.
 *
 * Double-encoding survives uvicorn's decode, so the handler receives the
 * still-single-encoded name and recovers the original with
 * `urllib.parse.unquote()`. Names WITHOUT reserved characters round-trip
 * identically either way, so this is safe to apply uniformly.
 *
 * ONLY use this for endpoints whose handler calls `unquote()` on the
 * path param. Today that is:
 *   - `PATCH/DELETE /api/teams/{tid}/config/stations/{name}`
 *   - `PATCH/DELETE /api/cycles/{cid}/config/stations/{name}`
 *   - `PUT/DELETE   /api/teams/{tid}/setup/station-workload-weights/{name}`
 *   - `PATCH/DELETE /api/teams/{tid}/date-request-categories/{key}`
 *
 * For a segment that is already URL-safe (a UUID, an opaque token) use
 * plain `encodeURIComponent` — double-encoding it would be misleading.
 */
export function encodePathName(name: string): string {
  return encodeURIComponent(encodeURIComponent(name));
}
